import { ProductImageApiError } from '@/api/ProductImages/ProductImageApiError';
import type {
  DailyImageBatch,
  GenerationRecord,
  ImageDescriptor,
  ImageBatchItem,
  ProductImageDetail,
  ProductImageSummary,
  PagedImages,
} from '@/api/ProductImages/types';

const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const identity = (value: unknown) =>
  typeof value === 'string' && value.length > 0;
const nullableIdentity = (value: unknown) => value === null || identity(value);
const requireShape = (valid: boolean) => {
  if (!valid)
    throw new ProductImageApiError(
      502,
      'The vehicle imagery response is invalid. Refresh after the API and Web update is complete.',
    );
};

export const validateImageDescriptor = (
  image: ImageDescriptor,
): ImageDescriptor => {
  requireShape(
    object(image) &&
      identity(image.assetId) &&
      nullableIdentity(image.generationId) &&
      Array.isArray(image.variantResults) &&
      (image.canSuspend === undefined ||
        typeof image.canSuspend === 'boolean') &&
      (image.suspensionUnavailableReason == null ||
        typeof image.suspensionUnavailableReason === 'string') &&
      (image.suspension == null ||
        (object(image.suspension) &&
          identity(image.suspension.suspendedAtUtc) &&
          identity(image.suspension.suspendedBy) &&
          identity(image.suspension.reason))),
  );
  for (const result of image.variantResults) {
    requireShape(
      object(result) &&
        identity(result.variantKey) &&
        typeof result.canRetry === 'boolean' &&
        (result.generation === null || object(result.generation)),
    );
    if (result.generation) validateGeneration(result.generation);
  }
  return image;
};

export const validateGeneration = (
  generation: GenerationRecord,
): GenerationRecord => {
  requireShape(
    object(generation) &&
      identity(generation.id) &&
      identity(generation.variantKey) &&
      nullableIdentity(generation.parentGenerationId) &&
      Array.isArray(generation.candidates),
  );
  if (generation.image)
    requireShape(
      object(generation.image) &&
        identity(generation.image.assetId) &&
        identity(generation.image.sourceRevisionId) &&
        identity(generation.image.variantKey),
    );
  if (generation.outputImage) validateImageDescriptor(generation.outputImage);
  return generation;
};

export const validateProductSummary = (
  product: ProductImageSummary,
): ProductImageSummary => {
  requireShape(
    object(product) &&
      (product.latestGeneration === null || object(product.latestGeneration)),
  );
  if (product.latestGeneration) validateGeneration(product.latestGeneration);
  if (product.currentImage) validateImageDescriptor(product.currentImage);
  return product;
};

export const validateProductDetail = (
  detail: ProductImageDetail,
): ProductImageDetail => {
  requireShape(
    object(detail) &&
      Array.isArray(detail.generations) &&
      Array.isArray(detail.revisions),
  );
  validateProductSummary(detail.product);
  if (detail.currentImage) validateImageDescriptor(detail.currentImage);
  detail.revisions.forEach(validateImageDescriptor);
  detail.generations.forEach(validateGeneration);
  return detail;
};

export const validateBatch = (batch: DailyImageBatch): DailyImageBatch => {
  requireShape(object(batch) && Array.isArray(batch.variantCounts));
  for (const counts of batch.variantCounts)
    requireShape(
      object(counts) &&
        identity(counts.variantKey) &&
        [
          'pendingCount',
          'succeededCount',
          'failedCount',
          'unknownCount',
          'stoppedCount',
        ].every((key) => typeof counts[key] === 'number'),
    );
  return batch;
};

export const validateBatchItem = (item: ImageBatchItem): ImageBatchItem => {
  requireShape(object(item) && nullableIdentity(item.generationId));
  if (item.outputImage) validateImageDescriptor(item.outputImage);
  return item;
};

export const validatePage = <T>(
  page: PagedImages<T>,
  validate: (item: T) => T,
) => {
  requireShape(object(page) && Array.isArray(page.items));
  page.items.forEach(validate);
};
